// based on https://github.com/ivoilic/zustand-ards

import { useMemo } from 'react';
import { pickAll, prop } from 'ramda';
import { shallow } from 'zustand/shallow';
import { EqualityFn, SmartStoreHook, StoreHook } from '../../types';

/**
 * Allows using smart selectors in created store.
 * Selector may accept one of as first arg:
 * 1. A string or a number: a property of the provided name is selected.
 * 2. A function: function is a selector itself, takes whole store as an argument which should produce selected data. Compare function is default - Object.is.
 * 3. An array of strings or numbers: selects props with provided names, result is an object and compare function is `shallow` by default.
 * 4. Empty (undefined): returns whole store. NOT RECOMMENDED.
 *
 * Second argument is an optional compare function which decides if result is actually changed or not. By default, `Object.is` is used. Most common option here is `shallow` (present in 'zustand/shallow' package), but you may provide any arbitrary function with matching interface. Be aware, however, unnecessary comparisons may take heavy tax on performance.
 */

export const withSmartSelectors = <State>(storeHook: StoreHook<State>) => {
  const smartStoreHookRaw = <Key extends keyof State, RetVal>(
    selector?: Key | Key[] | ((s: State) => RetVal),
    equals?: EqualityFn
  ) => {
    // handle empty selector separately - without equalityFn
    if (selector == null) {
      return storeHook();
    }

    // handle function selector
    if (typeof selector === 'function') {
      return storeHook(selector, equals);
    }

    // handle single key selector
    if (
      typeof selector === 'string' ||
      typeof selector === 'number' ||
      typeof selector === 'symbol'
    ) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const memSelector = useMemo(() => prop(selector), [selector]);
      return storeHook(memSelector, equals);
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const memArraySelector = useMemo(
      () => pickAll<State, Key>(selector),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [...(selector as Key[])]
    );
    // handle array of keys - shallow equality by default
    return storeHook<Pick<State, Key>>(memArraySelector, equals ?? shallow);
  };

  const smartStoreHook = smartStoreHookRaw as SmartStoreHook<State>;

  smartStoreHook.getState = storeHook.getState;
  smartStoreHook.getInitialState = storeHook.getInitialState;
  smartStoreHook.setInitialState = storeHook.setInitialState;
  smartStoreHook.setState = storeHook.setState;
  smartStoreHook.subscribe = storeHook.subscribe;

  return smartStoreHook;
};
